import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import { refusalFrom } from "./refusal";

import { PERSONAL_REQUEST_KINDS } from "../lib/customerWords";

export type RequestKind = typeof PERSONAL_REQUEST_KINDS[number];
export interface PersonalRequest {
  id: string;
  kind: RequestKind;
  state: "recorded" | "cancelled_by_requester";
  received_at: string;
  changed_at: string;
  as_of: string;
  elapsed_seconds: number | null;
}
interface RequestPage {
  requests: PersonalRequest[];
  has_more: boolean;
}
interface RequestWrite {
  request: PersonalRequest;
  repeated: boolean;
}

async function read<T>(response: Response): Promise<T> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw refusalFrom(response, body, "The personal request could not be read or recorded.");
  if (body === null || typeof body !== "object") throw new Error("The request response could not be read.");
  return body as T;
}

export function usePersonalRequests(offset: number) {
  return useQuery({
    queryKey: ["personal-requests", offset],
    retry: false,
    gcTime: 0,
    staleTime: 0,
    queryFn: async () => read<RequestPage>(await apiFetch(`/api/v1/privacy/requests?offset=${offset}`)),
  });
}

export function useRecordPersonalRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (input: { client_request_id: string; kind: RequestKind }) =>
      read<RequestWrite>(await apiFetch("/api/v1/privacy/requests", {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(input),
      })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["personal-requests"] }),
  });
}

export function useCancelPersonalRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (requestId: string) =>
      read<RequestWrite>(await apiFetch(`/api/v1/privacy/requests/${requestId}/cancel`, { method: "POST" })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["personal-requests"] }),
  });
}
